import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';
import appConfig from '../../../appConfig';
import PrimaryIdetifierHelper from '../../Utils/Helper/PrimaryIdetifierHelper';
import ChatIcon from '../../../Assets/Images/chat_icon.png';
import "./styles.css";
import axios from 'axios';

const LiveChat = () => {
  const { currentLanguage } = useSelector((storeState) => storeState.language);

  const [content, SetContent] = useState({});
  const entitiesRedux = useSelector((storeState) => storeState?.entities);
  const authRedux = useSelector((storeState) => storeState?.auth);
  const employeeProfileRedux = useSelector((storeState) => storeState?.employeeProfile);
  const registerationRedux = useSelector((storeState) => storeState?.registeration);

  const dispatch = useDispatch();

  // ------------------------------------
  // Load multilingual content
  // ------------------------------------
  useEffect(() => {
    if (!currentLanguage) return;

    import(`./Content/${currentLanguage}.json`)
      .then(module => SetContent(module.default))
      .catch(err => console.error("Failed to load language file:", err));
  }, [currentLanguage]);

  // ------------------------------------
  // Extract data with full null safety
  // ------------------------------------
  let personalInfo =
    entitiesRedux?.response?.onLoadResponseList?.PersonalInformation?.personalInformation ||
    employeeProfileRedux?.employeeProfile ||
    null;

  const employmentInfo =
    entitiesRedux?.response?.onLoadResponseList?.EmploymentInformation?.employmentInformation ||
    null;

  const employeeIdFromRedux = employmentInfo?.employeeId || null;
  const userbUID = authRedux?.loggedUserEntity?.data?.person?.bUID || null;

  const legalEntityData =
    authRedux?.loggedUserEntity?.data?.personDetails?._Result?.[0]?.legalEntity?.identifier
      ?.externalClientId?.externalClientId || "";

  const legalEntityBackup = registerationRedux?.legalEntityId || "";

  // Safe oneCode extraction
  const oneCode =
    typeof legalEntityBackup === "string" && legalEntityBackup.length > 0
      ? legalEntityBackup.split(":")[0]
      : (legalEntityData || "");

  const primaryIdetifierHelper = new PrimaryIdetifierHelper();

  // -----------------------------------------------------------
  // SAFE Snyk-Compliant Salesforce POST Chat Launcher
  // -----------------------------------------------------------
 const openSalesforceChat = (chatUrl, payload) => {
  if (!chatUrl) {
    console.error("Cannot open chat: chatUrl is missing");
    return;
  }

  // Build pure HTML with properly escaped attribute values
  const escape = (v) =>
    String(v || "").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const inputs = Object.entries(payload)
    .map(([key, value]) => {
      return `<input type="hidden" name="${escape(key)}" value="${escape(value)}">`;
    })
    .join("");

  const html = `
    <html>
      <body>
        <form id="sf_form" method="POST" action="${escape(chatUrl)}">
          ${inputs}
        </form>
        <script>
          document.getElementById("sf_form").submit();
        </script>
      </body>
    </html>
  `;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);

  // createObjectURL always returns a blob: URL — validate before opening
  if (typeof url !== 'string' || !url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
    return;
  }

  // Open a blank window first (no remote-derived URL passed to window.open),
  // then write the isolated HTML into it so the form auto-submits.
  const newWin = window.open('', '_blank');
  if (newWin) {
    newWin.document.write(html);
    newWin.document.close();
  }

  // Revoke the blob URL immediately — it is no longer needed
  URL.revokeObjectURL(url);
};


  // -----------------------------------------------------------
  // Call backend → get chat URL → open Salesforce chat window
  // -----------------------------------------------------------
  const sendChatHandlerReq = async (details) => {
    try {
      const resp = await axios.post(appConfig.SSP_MID_URL + '/getSalesForceChat');

      // Flexible extraction so silently changing backend won't break
      const chatURL =
        resp?.data?.chatURL?.chaturl ||
        resp?.data?.chatURL ||
        resp?.data?.chaturl ||
        "";

      if (!chatURL) {
        console.error("Chat URL missing in backend response:", resp?.data);
        return;
      }

      try {
        const parsedChatUrl = new URL(chatURL);
        if (parsedChatUrl.protocol !== 'https:') {
          console.error("Blocked unsafe chat URL protocol:", parsedChatUrl.protocol);
          return;
        }
      } catch {
        console.error("Invalid chat URL received from backend:", chatURL);
        return;
      }

      const payload = {
        Source: "LIFESTYLE",
        ContactIdentifier: details?.contactId || "",
        AccountIdentifier: details?.accountCode || "",
        FirstName: details?.firstName || "",
        LastName: details?.lastName || "",
        State: details?.state || "",
        Phone: details?.phone || ""
      };
      openSalesforceChat(chatURL, payload);

    } catch (err) {
      console.error("Error in sendChatHandlerReq:", err?.message || err);
    }
  };

  // -----------------------------------------------------------
  // Authenticated chat
  // -----------------------------------------------------------
  const manageAuthenticatedUser = () => {
    if (!employeeIdFromRedux || !personalInfo || !oneCode) {
      console.warn("Falling back to unauthenticated chat (incomplete auth data)");
      return manageUnAuthenticatedUser();
    }

    const primaryIdData = primaryIdetifierHelper.getPersonalDetails(
      employeeIdFromRedux,
      personalInfo,
      oneCode
    );

    if (!primaryIdData) {
      console.warn("Helper returned null → fallback to guest chat");
      return manageUnAuthenticatedUser();
    }

    const personalDetails = {
      contactId: JSON.stringify({
        contactLastName: primaryIdData?.lastName || "",
        zipCode: primaryIdData?.zipCode || "",
        last4epmembernumber: primaryIdData?.employeeId || "",
        dateofbirth: primaryIdData?.dateOfBirth || ""
      }),
      accountCode: oneCode || "",
      firstName: primaryIdData?.firstName || "",
      lastName: primaryIdData?.lastName || "",
      state: primaryIdData?.state || "",
      phone: primaryIdData?.personalPhone || ""
    };
    sendChatHandlerReq(personalDetails);
  };

  // -----------------------------------------------------------
  // Guest chat
  // -----------------------------------------------------------
  const manageUnAuthenticatedUser = () => {
    const personalDetails = {
      contactId: "",
      accountCode: oneCode || "",
      firstName: "",
      lastName: "",
      state: "",
      phone: ""
    };

    sendChatHandlerReq(personalDetails);
  };

  // -----------------------------------------------------------
  // Chat click handler
  // -----------------------------------------------------------
  const onChatClick = (e) => {
    e.preventDefault();

    if (userbUID && oneCode && employeeIdFromRedux && personalInfo) {
      manageAuthenticatedUser();
    } else {
      manageUnAuthenticatedUser();
    }
  };

  return (
    <aside role="complementary">
      <a
        href="#"
        className='chat-link'
        onClick={onChatClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img className='chat-icon' src={ChatIcon} alt='Chat Icon' />
      </a>
    </aside>
  );
};

export default LiveChat;
